// A creator's own stats page, /partner/<token>: what their link brought in and what they've earned.
// Totals only. The token in the link is the only key, so it's only shared with the creator.
// The deal and earnings are about a fee share, so the server only sends them while pricing is on.
import { fmt } from '../../money.ts';
import { brand } from '../../brand.ts';
import { partnerStats, type PartnerStats } from '../api.ts';
import { app, logo, copy, esc, target } from '../dom.ts';
import { errorScreen } from './shared.ts';

export function partnerView(token: string): void {
  document.title = `Your ${brand.name} stats`;
  app.innerHTML = `<div class="bar">${logo}<span>Partner</span></div><div class="body"><p class="loading">Loading…</p></div>`;
  partnerStats(token).then((p) => {
    const caption = partnerCaption(p);
    app.innerHTML = `
      <div class="bar">${logo}<span>Partner · ${esc(p.code)}</span></div>
      <div class="body stack">
        <div><span class="lbl">${esc(p.label)}</span><h1 class="h">Your link so far</h1></div>
        <div class="kpis">
          <div class="kpi"><div class="sub">Visits</div><div class="kpi-v amt">${p.visits}</div></div>
          <div class="kpi"><div class="sub">New payers</div><div class="kpi-v amt">${p.newPayers}</div><div class="kpi-s">their first split used your code</div></div>
          <div class="kpi"><div class="sub">Splits</div><div class="kpi-v amt">${p.splits}</div></div>
          <div class="kpi"><div class="sub">Friends reached</div><div class="kpi-v amt">${p.friendsReached}</div><div class="kpi-s">at those tables</div></div>
          ${p.terms ? `<div class="kpi"><div class="sub">You've earned</div><div class="kpi-v amt">${fmt(p.earnedCents)}</div><div class="kpi-s">${p.feesLive ? 'your share so far' : 'estimated until fees start'}</div></div>` : ''}
        </div>
        ${p.terms || p.offer ? `<div class="card stack" style="gap:8px">
          <div class="lbl">Your deal</div>
          ${p.terms ? `<p style="margin:0">You get ${esc(p.terms)}.${p.feesLive ? '' : ' Fees aren\'t on yet, so it\'s tracked now and paid once they start.'}</p>` : ''}
          ${p.payerCap ? `<div class="sub">${Math.min(p.newPayers, p.payerCap)} of ${p.payerCap} spots used</div>` : ''}
          ${p.offer ? `<p style="margin:0">Your followers get: <b>${esc(p.offer)}</b>.</p>` : ''}
        </div>` : ''}
        <div class="card stack" style="gap:8px">
          <div class="lbl">Post this</div>
          <p class="sub" style="margin:0;white-space:pre-wrap;overflow-wrap:anywhere">${esc(caption)}</p>
          <button class="btn" data-act="caption">Copy caption</button>
        </div>
        <div class="card stack" style="gap:8px">
          <div class="lbl">Your link</div>
          <div class="amt" style="word-break:break-all;font-size:14px">${esc(p.link)}</div>
          <button class="btn2" data-act="copy">Copy link</button>
        </div>
      </div>`;
    app.onclick = (e) => {
      if (target(e, '[data-act=copy]')) void copy(p.link);
      if (target(e, '[data-act=caption]')) void copy(caption);
    };
  }).catch((err: Error) => errorScreen(err.message));
}

/** A caption the creator can post as is: what the app does, their followers' perk, and their link. */
export const partnerCaption = (p: Pick<PartnerStats, 'offer' | 'link'>) =>
  `${brand.oneLiner}${p.offer ? `\n\n${p.offer} with my link:` : '\n\nMy link:'} ${p.link}`;
